import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ChevronRight, ChevronLeft, Check, Loader, FileText, Upload, AlertTriangle } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = ['Datos Personales', 'Categorías', 'Resumen y Confirmación'];

export const Inscripcion = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [precios, setPrecios] = useState({});
  const [grupos, setGrupos] = useState({});
  const [codigoCupon, setCodigoCupon] = useState('');
  const [cuponValido, setCuponValido] = useState(false);
  const [precioCalculado, setPrecioCalculado] = useState(null);
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [comprobanteFile, setComprobanteFile] = useState(null);
  const [comprobanteError, setComprobanteError] = useState('');
  const [submitAttemptedWithoutFile, setSubmitAttemptedWithoutFile] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    cedula: '',
    numero_competicion: '',
    celular: '',
    correo: '',
    liga: '',
    categorias: [],
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchCategorias();
  }, []);

  useEffect(() => {
    if (formData.categorias.length > 0) {
      calcularPrecio();
    }
  }, [formData.categorias, codigoCupon]);

  const fetchCategorias = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategorias(response.data.categorias);
      setPrecios(response.data.precios);
      setGrupos(response.data.grupos || {});
    } catch (error) {
      console.error('Error fetching categorias:', error);
    }
  };

  const calcularPrecio = async () => {
    try {
      const response = await axios.post(`${API}/registrations/calculate`, {
        categorias: formData.categorias,
        codigo_cupon: codigoCupon || null,
      });
      setPrecioCalculado(response.data);
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  };

  const validarCupon = async () => {
    if (!codigoCupon) return;
    try {
      const response = await axios.post(`${API}/coupons/validate`, {
        codigo: codigoCupon,
      });
      if (response.data.valido) {
        setCuponValido(true);
        calcularPrecio();
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Cupón inválido');
      setCuponValido(false);
    }
  };

  const validateStep = () => {
    const newErrors = {};

    if (currentStep === 0) {
      if (!formData.nombre) newErrors.nombre = 'Nombre requerido';
      if (!formData.apellido) newErrors.apellido = 'Apellido requerido';
      if (!formData.cedula) newErrors.cedula = 'Cédula requerida';
      if (formData.cedula && formData.cedula.length < 5) {
        newErrors.cedula = 'La cédula debe tener al menos 5 caracteres';
      }
      if (!formData.numero_competicion) newErrors.numero_competicion = 'Número de competición requerido';
      if (!formData.celular) {
        newErrors.celular = 'Celular requerido';
      } else if (formData.celular.replace(/\D/g, '').length < 10) {
        newErrors.celular = 'El celular debe tener al menos 10 dígitos';
      }
      if (!formData.correo) newErrors.correo = 'Correo requerido';
      if (formData.correo && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.correo)) {
        newErrors.correo = 'Correo inválido';
      }
    }

    if (currentStep === 1) {
      if (formData.categorias.length === 0) {
        newErrors.categorias = 'Debe seleccionar al menos una categoría';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
    }
  };

  const handlePrev = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleCategoriaToggle = (categoria) => {
    setFormData((prev) => {
      const categorias = prev.categorias.includes(categoria)
        ? prev.categorias.filter((c) => c !== categoria)
        : [...prev.categorias, categoria];
      return { ...prev, categorias };
    });
  };

  const handleComprobanteChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setComprobanteFile(null);
      return;
    }
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    const maxSizeMB = 10;
    if (!allowedTypes.includes(file.type)) {
      setComprobanteError('Formato no permitido. Usa JPG, PNG, GIF, WEBP o PDF.');
      setComprobanteFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      setComprobanteError(`El archivo supera ${maxSizeMB}MB.`);
      setComprobanteFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setComprobanteError('');
    setComprobanteFile(file);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    if (!aceptaTerminos) {
      alert('Debes aceptar los Términos y Condiciones para continuar');
      return;
    }

    // El comprobante es OBLIGATORIO. Si no hay archivo adjunto, bloqueamos el envío.
    if (!comprobanteFile) {
      setSubmitAttemptedWithoutFile(true);
      alert('⚠️ COMPROBANTE DE PAGO OBLIGATORIO\n\nPara completar tu inscripción es indispensable adjuntar el comprobante de pago.\n\nPor favor, sube tu comprobante (JPG, PNG o PDF) antes de enviar el formulario.');
      return;
    }

    setLoading(true);
    try {
      // 1. Create the registration (JSON payload)
      const response = await axios.post(`${API}/registrations`, {
        ...formData,
        codigo_cupon: codigoCupon || null,
      });

      const registration = response.data;

      // 2. Upload the comprobante (required)
      try {
        const fileForm = new FormData();
        fileForm.append('file', comprobanteFile);
        await axios.post(
          `${API}/registrations/${registration.id}/upload-comprobante`,
          fileForm,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      } catch (uploadErr) {
        console.error('Error subiendo comprobante:', uploadErr);
        alert('La preinscripción se creó correctamente, pero hubo un problema subiendo el comprobante. Contacta al administrador por WhatsApp.');
      }

      // 3. Redirect to pre-registration confirmation page
      navigate('/confirmacion-preinscripcion?registration_id=' + registration.id);
    } catch (error) {
      console.error('Error:', error);
      let errorMessage = 'Error al procesar inscripción';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          const fieldErrors = detail.map(err => {
            const field = err.loc?.slice(-1)[0] || 'campo';
            const fieldNames = {
              'nombre': 'Nombre',
              'apellido': 'Apellido', 
              'cedula': 'Cédula',
              'numero_competicion': 'Número de competición',
              'celular': 'Celular',
              'correo': 'Correo',
              'categorias': 'Categorías'
            };
            const friendlyField = fieldNames[field] || field;
            
            if (err.type === 'string_too_short') {
              const minLen = err.ctx?.min_length || 0;
              return `${friendlyField} debe tener al menos ${minLen} caracteres`;
            }
            return `${friendlyField}: ${err.msg}`;
          });
          errorMessage = fieldErrors.join('\n');
        } else if (typeof detail === 'object') {
          errorMessage = detail.message || JSON.stringify(detail);
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="font-heading text-6xl md:text-7xl font-black uppercase tracking-tighter text-glow-red mb-4" data-testid="inscripcion-title">
            INSCRIPCIÓN
          </h1>
          <p className="text-white/80 text-lg">
            Completa el formulario para asegurar tu lugar en el campeonato.
          </p>
        </div>

        <div className="mb-8">
          <div className="flex justify-between items-center">
            {STEPS.map((step, index) => (
              <div key={index} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    data-testid={`step-indicator-${index}`}
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-heading font-bold border-2 transition-colors ${
                      index <= currentStep
                        ? 'bg-primary border-primary text-white'
                        : 'bg-surface border-white/20 text-white/50'
                    }`}
                  >
                    {index < currentStep ? <Check className="w-5 h-5" /> : index + 1}
                  </div>
                  <span className="text-xs mt-2 font-heading uppercase text-center">{step}</span>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`h-0.5 flex-1 mx-2 ${
                      index < currentStep ? 'bg-primary' : 'bg-white/20'
                    }`}
                  ></div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-surface border border-white/10 p-8">
          {currentStep === 0 && (
            <div className="space-y-6" data-testid="step-datos-personales">
              <h2 className="font-heading text-2xl font-bold uppercase mb-6">Datos Personales</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-heading font-bold mb-2">Nombre *</label>
                  <input
                    type="text"
                    name="nombre"
                    data-testid="input-nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    className="w-full bg-black/50 border border-white/20 focus:border-primary text-white h-12 px-4 outline-none"
                  />
                  {errors.nombre && <p className="text-primary text-sm mt-1">{errors.nombre}</p>}
                </div>

                <div>
                  <label className="block text-sm font-heading font-bold mb-2">Apellido *</label>
                  <input
                    type="text"
                    name="apellido"
                    data-testid="input-apellido"
                    value={formData.apellido}
                    onChange={handleInputChange}
                    className="w-full bg-black/50 border border-white/20 focus:border-primary text-white h-12 px-4 outline-none"
                  />
                  {errors.apellido && <p className="text-primary text-sm mt-1">{errors.apellido}</p>}
                </div>

                <div>
                  <label className="block text-sm font-heading font-bold mb-2">Cédula *</label>
                  <input
                    type="text"
                    name="cedula"
                    data-testid="input-cedula"
                    value={formData.cedula}
                    onChange={handleInputChange}
                    className="w-full bg-black/50 border border-white/20 focus:border-primary text-white h-12 px-4 outline-none"
                  />
                  {errors.cedula && <p className="text-primary text-sm mt-1">{errors.cedula}</p>}
                </div>

                <div>
                  <label className="block text-sm font-heading font-bold mb-2">Número de Competición *</label>
                  <input
                    type="text"
                    name="numero_competicion"
                    data-testid="input-numero-competicion"
                    value={formData.numero_competicion}
                    onChange={handleInputChange}
                    className="w-full bg-black/50 border border-white/20 focus:border-primary text-white h-12 px-4 outline-none"
                  />
                  {errors.numero_competicion && <p className="text-primary text-sm mt-1">{errors.numero_competicion}</p>}
                </div>

                <div>
                  <label className="block text-sm font-heading font-bold mb-2">Celular * <span className="text-white/50 font-normal">(10 dígitos)</span></label>
                  <input
                    type="tel"
                    name="celular"
                    data-testid="input-celular"
                    value={formData.celular}
                    onChange={handleInputChange}
                    placeholder="Ej: 3001234567"
                    maxLength={15}
                    className="w-full bg-black/50 border border-white/20 focus:border-primary text-white h-12 px-4 outline-none"
                  />
                  <div className="flex justify-between mt-1">
                    {errors.celular ? (
                      <p className="text-primary text-sm">{errors.celular}</p>
                    ) : (
                      <span></span>
                    )}
                    <span className={`text-xs ${formData.celular.replace(/\D/g, '').length >= 10 ? 'text-green-500' : 'text-white/50'}`}>
                      {formData.celular.replace(/\D/g, '').length}/10 dígitos
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-heading font-bold mb-2">Correo Electrónico *</label>
                  <input
                    type="email"
                    name="correo"
                    data-testid="input-correo"
                    value={formData.correo}
                    onChange={handleInputChange}
                    className="w-full bg-black/50 border border-white/20 focus:border-primary text-white h-12 px-4 outline-none"
                  />
                  {errors.correo && <p className="text-primary text-sm mt-1">{errors.correo}</p>}
                </div>

                <div>
                  <label className="block text-sm font-heading font-bold mb-2">Liga a la que está afiliado</label>
                  <input
                    type="text"
                    name="liga"
                    data-testid="input-liga"
                    value={formData.liga}
                    onChange={handleInputChange}
                    placeholder="Ej: Liga del Cauca"
                    className="w-full bg-black/50 border border-white/20 focus:border-primary text-white h-12 px-4 outline-none"
                  />
                </div>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div data-testid="step-categorias">
              <h2 className="font-heading text-2xl font-bold uppercase mb-6">Selecciona tus Categorías</h2>
              <p className="text-white/70 mb-6">Puedes seleccionar múltiples categorías si cumples los requisitos.</p>
              
              {errors.categorias && (
                <div className="bg-primary/20 border border-primary p-4 mb-6">
                  <p className="text-primary font-bold">{errors.categorias}</p>
                </div>
              )}

              <div className="max-h-[60vh] overflow-y-auto pr-2 space-y-6">
                {(() => {
                  // Preferred display order; any extra groups not in this list
                  // are appended at the end so new admin groups still appear.
                  const preferredOrder = ['VELOCIDAD TOP', 'VELOCIDAD', 'VELOCIDAD RECREATIVAS', 'KARTS', 'VELOTIERRA', 'MOTOCROSS', 'EVENTOS ESPECIALES'];
                  const groupKeys = Object.keys(grupos || {});
                  const ordered = [
                    ...preferredOrder.filter(g => groupKeys.includes(g)),
                    ...groupKeys.filter(g => !preferredOrder.includes(g)),
                  ];
                  return ordered;
                })().map((groupName) => {
                  const groupCats = grupos[groupName] || [];
                  if (groupCats.length === 0) return null;
                  
                  const groupColors = {
                    'VELOCIDAD TOP': 'border-primary bg-primary/20 text-primary',
                    'VELOCIDAD': 'border-secondary bg-secondary/20 text-secondary',
                    'VELOCIDAD RECREATIVAS': 'border-accent bg-accent/20 text-accent',
                    'KARTS': 'border-warning bg-warning/20 text-warning',
                    'VELOTIERRA': 'border-orange-500 bg-orange-500/20 text-orange-500',
                    'MOTOCROSS': 'border-green-500 bg-green-500/20 text-green-500',
                    'EVENTOS ESPECIALES': 'border-purple-500 bg-purple-500/20 text-purple-500'
                  };
                  const colorClass = groupColors[groupName] || 'border-white/30 bg-white/10 text-white';
                  
                  return (
                    <div key={groupName} className="mb-4">
                      <div className={`${colorClass} border-l-4 px-4 py-2 mb-3`}>
                        <h3 className="font-heading font-bold uppercase text-sm">{groupName}</h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {groupCats.map((categoria, index) => (
                          <div
                            key={`${groupName}-${index}`}
                            data-testid={`categoria-checkbox-${groupName}-${index}`}
                            onClick={() => handleCategoriaToggle(categoria)}
                            className={`p-3 border-2 cursor-pointer transition-all ${
                              formData.categorias.includes(categoria)
                                ? 'border-primary bg-primary/10'
                                : 'border-white/20 hover:border-white/40'
                            }`}
                          >
                            <div className="flex items-start space-x-3">
                              <div
                                className={`w-5 h-5 border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${
                                  formData.categorias.includes(categoria)
                                    ? 'border-primary bg-primary'
                                    : 'border-white/40'
                                }`}
                              >
                                {formData.categorias.includes(categoria) && <Check className="w-3 h-3 text-white" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-heading font-bold text-sm truncate">{categoria}</p>
                                <p className="text-white/70 text-xs mt-1">
                                  COP {precios[categoria]?.toLocaleString() || '100.000'}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div data-testid="step-resumen">
              <h2 className="font-heading text-2xl font-bold uppercase mb-6">Resumen de Inscripción</h2>
              
              <div className="space-y-6">
                <div className="bg-black/50 p-6 border border-white/10">
                  <h3 className="font-heading font-bold mb-4">Datos del Piloto</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-white/70">Nombre:</p>
                      <p className="font-bold">{formData.nombre} {formData.apellido}</p>
                    </div>
                    <div>
                      <p className="text-white/70">Cédula:</p>
                      <p className="font-bold">{formData.cedula}</p>
                    </div>
                    <div>
                      <p className="text-white/70">Número:</p>
                      <p className="font-bold">{formData.numero_competicion}</p>
                    </div>
                    <div>
                      <p className="text-white/70">Celular:</p>
                      <p className="font-bold">{formData.celular}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-black/50 p-6 border border-white/10">
                  <h3 className="font-heading font-bold mb-4">Categorías Seleccionadas ({formData.categorias.length})</h3>
                  <ul className="space-y-2">
                    {formData.categorias.map((cat, idx) => (
                      <li key={idx} className="flex items-center space-x-2">
                        <span className="text-primary">•</span>
                        <span>{cat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-black/50 p-6 border border-white/10">
                  <h3 className="font-heading font-bold mb-4">Cupón de Descuento (Opcional)</h3>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      data-testid="input-cupon"
                      value={codigoCupon}
                      onChange={(e) => setCodigoCupon(e.target.value.toUpperCase())}
                      placeholder="CODIGO123"
                      className="flex-1 bg-black/50 border border-white/20 focus:border-secondary text-white h-12 px-4 outline-none uppercase"
                    />
                    <button
                      onClick={validarCupon}
                      data-testid="btn-validar-cupon"
                      className="bg-secondary text-black font-heading font-bold uppercase px-6 hover:bg-secondary/80 transition-colors"
                    >
                      Validar
                    </button>
                  </div>
                  {cuponValido && (
                    <p className="text-secondary text-sm mt-2">✓ Cupón aplicado correctamente</p>
                  )}
                </div>

                {precioCalculado && (
                  <div className="bg-primary/20 border-2 border-primary p-6">
                    <h3 className="font-heading text-xl font-bold uppercase mb-4">Desglose de Precio</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Precio Base:</span>
                        <span className="font-bold">COP {precioCalculado.precio_base.toLocaleString()}</span>
                      </div>
                      {precioCalculado.descuento > 0 && (
                        <div className="flex justify-between text-secondary">
                          <span>Descuento:</span>
                          <span className="font-bold">- COP {precioCalculado.descuento.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="border-t border-white/20 pt-2 mt-2">
                        <div className="flex justify-between text-xl">
                          <span className="font-heading font-black uppercase">Total a Pagar:</span>
                          <span className="font-heading font-black text-primary">
                            COP {precioCalculado.precio_final.toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <div className="text-sm text-white/70 mt-2">
                        Fase actual: <span className="font-bold uppercase">{precioCalculado.fase_actual}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Comprobante de Pago (Obligatorio) — moved above Payment Info for visibility */}
                <div className="bg-black/50 border border-secondary/40 p-6" data-testid="comprobante-section">
                  <h3 className="font-heading font-bold mb-2 flex items-center space-x-2">
                    <Upload className="w-5 h-5 text-secondary" />
                    <span>Comprobante de Pago (Obligatorio)</span>
                  </h3>
                  <p className="text-white/80 text-sm mb-3">
                    <strong className="text-red-400">Importante:</strong> debes adjuntar tu comprobante de pago para poder enviar la inscripción.
                  </p>
                  <p className="text-white/60 text-xs mb-4">
                    Una vez adjunto, tu inscripción quedará marcada como <strong className="text-yellow-400">PENDIENTE POR VERIFICAR</strong>. Cuando el administrador valide el pago, recibirás la confirmación oficial.
                  </p>

                  <label
                    htmlFor="comprobante-input"
                    className="flex items-center justify-center gap-2 cursor-pointer bg-secondary/10 hover:bg-secondary/20 border-2 border-dashed border-secondary/50 text-secondary font-heading font-bold uppercase px-4 py-4 transition-colors"
                    data-testid="comprobante-dropzone"
                  >
                    <Upload className="w-5 h-5" />
                    <span>{comprobanteFile ? 'Cambiar archivo' : 'Adjuntar comprobante (JPG, PNG, PDF)'}</span>
                  </label>
                  <input
                    id="comprobante-input"
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                    onChange={handleComprobanteChange}
                    className="hidden"
                    data-testid="input-comprobante"
                  />

                  {comprobanteFile && (
                    <div className="mt-3 flex items-center justify-between bg-green-500/10 border border-green-500/30 px-4 py-2" data-testid="comprobante-selected">
                      <div className="flex items-center space-x-2 min-w-0">
                        <FileText className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <span className="text-green-300 text-sm truncate">{comprobanteFile.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setComprobanteFile(null);
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        className="text-xs text-white/70 hover:text-white uppercase font-heading ml-3 flex-shrink-0"
                        data-testid="btn-remove-comprobante"
                      >
                        Quitar
                      </button>
                    </div>
                  )}

                  {comprobanteError && (
                    <p className="text-red-400 text-sm mt-2 flex items-center space-x-1" data-testid="comprobante-error">
                      <AlertTriangle className="w-4 h-4" />
                      <span>{comprobanteError}</span>
                    </p>
                  )}

                  {!comprobanteFile && submitAttemptedWithoutFile && (
                    <p className="text-red-400 text-sm mt-3 flex items-start space-x-2" data-testid="comprobante-warning">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>Debes adjuntar el comprobante de pago. Sin este archivo no se puede completar la inscripción.</span>
                    </p>
                  )}
                </div>

                {/* Payment Info */}
                <div className="bg-green-500/10 border border-green-500/30 p-6">
                  <h3 className="font-heading font-bold text-green-400 mb-3">Información de Pago</h3>
                  <p className="text-white/80 mb-2">
                    El pago se realizará mediante transferencia a <strong className="text-green-400">Nequi: 3104223288</strong>
                  </p>
                  <p className="text-white/60 text-sm">
                    Si aún no has pagado, después de enviar el formulario recibirás las instrucciones completas para confirmar tu inscripción.
                  </p>
                </div>

                {/* Terms and Conditions Checkbox */}
                <div className="bg-black/50 border border-white/10 p-6">
                  <div 
                    className="flex items-start space-x-3 cursor-pointer"
                    onClick={() => setAceptaTerminos(!aceptaTerminos)}
                  >
                    <div
                      data-testid="checkbox-terminos"
                      className={`w-6 h-6 border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                        aceptaTerminos
                          ? 'border-secondary bg-secondary'
                          : 'border-white/40 hover:border-white/60'
                      }`}
                    >
                      {aceptaTerminos && <Check className="w-4 h-4 text-black" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-white/90">
                        He leído y acepto los{' '}
                        <Link 
                          to="/terminos-y-condiciones" 
                          target="_blank"
                          className="text-secondary hover:text-secondary/80 underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Términos y Condiciones
                        </Link>
                        , Política de Devolución y Cláusula de Exoneración de Responsabilidad.
                      </p>
                    </div>
                  </div>
                  {!aceptaTerminos && (
                    <p className="text-warning text-sm mt-3 flex items-center space-x-2">
                      <FileText className="w-4 h-4" />
                      <span>Debes aceptar los términos para continuar</span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
            {currentStep > 0 && (
              <button
                onClick={handlePrev}
                data-testid="btn-prev"
                className="flex items-center space-x-2 bg-surface text-white font-heading font-bold uppercase px-6 py-3 border border-white/20 hover:border-white transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Anterior</span>
              </button>
            )}

            {currentStep < STEPS.length - 1 ? (
              <button
                onClick={handleNext}
                data-testid="btn-next"
                className="ml-auto flex items-center space-x-2 bg-primary text-white font-heading font-bold uppercase px-6 py-3 hover:bg-primary/80 transition-colors"
              >
                <span>Siguiente</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading || !aceptaTerminos}
                data-testid="btn-submit"
                className={`ml-auto flex items-center space-x-2 font-heading font-black uppercase px-8 py-4 skew-racing hover:skew-x-0 transition-all border-2 ${
                  aceptaTerminos 
                    ? 'bg-primary text-white border-transparent hover:border-white' 
                    : 'bg-gray-600 text-gray-400 border-gray-500 cursor-not-allowed'
                }`}
              >
                <span className="unskew flex items-center space-x-2">
                  {loading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      <span>Procesando...</span>
                    </>
                  ) : (
                    <span>Enviar Preinscripción</span>
                  )}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
